import { Banner, Button, InputSelect } from '@vrplatform/components';
import { captureSentryError, useQuery, useTeam } from '@vrplatform/data';
import type { connection_bool_exp } from '@vrplatform/graphql';
import { Icon } from '@vrplatform/icons';
import type { Maybe } from '@vrplatform/utils';
import {
  Avatar,
  Box,
  Center,
  Group,
  InputWrapper,
  Modal,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { whereConnections } from '@vrplatform/ui-common';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { CsvImportModalConnection } from '../csv-import-modal';
import type { ConnectionExtractor } from '../extract-single-modal/useExtractSingleButtonQuery';

export const SelectConnectionByAppModal = ({
  app: initial,
  connections,
  closeModal: cm,
  onSubmit,
}: {
  app: {
    id: string;
    name: string;
    logo: Maybe<string>;
    extractors: ConnectionExtractor[];
  } | null;
  connections?: {
    id: string;
    name: string;
  }[];
  closeModal: () => void;
  onSubmit: (
    connection: CsvImportModalConnection & {
      extractors: ConnectionExtractor[];
    }
  ) => void | Promise<void>;
}) => {
  const [{ id: teamId }] = useTeam();
  const [submitError, setSubmitError] = useState<string | undefined>();

  type FormInputs = {
    connectionId: string | null;
  };
  const methods = useForm<FormInputs>();

  const closeModal = () => {
    cm();
    setSubmitError(undefined);
    methods.reset({
      connectionId: null,
    });
  };

  useEffect(() => {
    setSubmitError(undefined);
    methods.reset({
      connectionId: null,
    });
  }, [initial?.id, methods]);

  const submit = async (data: FormInputs) => {
    if (!data.connectionId || !initial) return;

    try {
      setSubmitError(undefined);

      await Promise.resolve(
        onSubmit({
          id: data.connectionId,
          app: {
            id: initial.id,
            name: initial.name,
            logo: initial.logo ?? undefined,
          },
          extractors: initial.extractors,
        })
      );

      closeModal();
    } catch (error) {
      captureSentryError(error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'We could not import the CSV file. Please try again later.'
      );
    }
  };

  const [debounced] = useDebouncedValue(initial, 500, { leading: true });

  const app = initial ?? debounced;

  return (
    <Modal
      opened={!!initial}
      onClose={closeModal}
      centered
      size={450}
      radius="md"
      styles={(theme) => ({
        content: {
          padding: theme.spacing.md,
          paddingTop: 0,
        },
      })}
    >
      <Center mb="md">
        {app?.logo ? (
          <Avatar
            src={app.logo}
            size={'md'}
            sx={(theme) => ({
              border: `1px solid ${theme.colors.gray[2]}`,
            })}
          />
        ) : (
          <Icon
            icon="CloudUploadIcon"
            color={(theme) => theme.colors[theme.primaryColor][8]}
            size={24}
          />
        )}
      </Center>

      <Title ta="center" order={3} mb="md">
        Select connection to import data to
      </Title>

      <Text component="p" c="gray" ta="center" mb="md">
        You have multiple connections for {initial?.name}.<br />
        Please select the connection that you want to associate with this
        import.
      </Text>

      <Box
        component="form"
        onSubmit={methods.handleSubmit(submit)}
        onReset={closeModal}
      >
        <Controller
          control={methods.control}
          name="connectionId"
          rules={{
            required: 'Please select a connection',
          }}
          render={({ field, fieldState }) => {
            const queryData = useQuery(
              (q, args) => {
                const where: connection_bool_exp = whereConnections({
                  teamId: args.teamId,
                  appId: args.appId,
                });

                return q
                  .connection({
                    where,
                    order_by: [{ name: 'asc_nulls_last' }],
                  })
                  .map((connection) => ({
                    value: connection.id,
                    label: connection.name || '',
                  }));
              },
              {
                skip: !initial || !teamId || !!connections,
                variables: {
                  teamId,
                  appId: initial?.id,
                },
              }
            );

            const options =
              connections?.map((connection) => ({
                value: connection.id,
                label: connection.name,
              })) ??
              queryData.data ??
              [];

            const value = field.value
              ? (options.find((x) => x.value === field.value) ?? null)
              : null;

            return (
              <InputWrapper
                mb={rem(40)}
                mt="lg"
                label="Connection"
                error={fieldState.error?.message}
              >
                <InputSelect
                  type="single"
                  value={value}
                  data={{
                    options,
                    loading: !connections && queryData.isLoading,
                    error: connections ? null : queryData.error,
                  }}
                  setValue={(v) => field.onChange(v?.value || null)}
                  inputProps={{
                    loadingQuery: !connections && queryData.isLoading,
                    error:
                      !!fieldState.error?.message ||
                      (!connections && queryData.error?.message),
                    placeholder: `Select ${initial?.name} connection`,
                    withClearButton: true,
                  }}
                  dropdownProps={{
                    withinPortal: true,
                    width: 'target',
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Banner
          mounted={!!submitError}
          message={
            submitError ??
            'We could not import the CSV file. Please try again later.'
          }
          mb="md"
          size="sm"
          title="We couldn't import your file"
          type="error"
        />

        <Group wrap="nowrap">
          <Button type="reset" disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={methods.formState.isSubmitting}
            sx={{
              flex: 1,
            }}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
